import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { addUser, updateUser, fetchUserById } from "../services/api";
import AdminLayout from "../components/layout/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AdminUserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEdit = !!id;

  const { 
    data: response, 
    isLoading: isFetching, 
    error: fetchError 
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => fetchUserById(id),
    enabled: isEdit,
    staleTime: 5 * 60 * 1000,
  });

  const user = response?.user;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    patientId: "",
    status: "active",
  });

  useEffect(() => {
    if (isEdit && user) {
      setFormData({
        name: user.full_name || user.name || "",
        email: user.email || "",
        phone: user.phone || user.phone_number || "",
        patientId: user.hospital_number || user.patientId || "",
        status: user.status || "active",
      });
    }
  }, [user, isEdit]);

  const addMutation = useMutation({
    mutationFn: addUser,
    onSuccess: () => {
      queryClient.invalidateQueries(["hospitalUsers"]);
      navigate("/admin/users");
    },
    onError: (err) => {
      alert("Failed to create user: " + (err.message || "Unknown error"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["hospitalUsers"]);
      queryClient.invalidateQueries(["user", id]);
      navigate("/admin/users");
    },
    onError: (err) => {
      alert("Failed to update user: " + (err.message || "Unknown error"));
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      alert("Name and Email are required fields.");
      return;
    }

    if (isEdit) {
      updateMutation.mutate({ id, data: formData });
    } else {
      addMutation.mutate(formData);
    }
  };

  if (isEdit && isFetching) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto py-12 px-4 text-center text-gray-600">
          Loading user information...
        </div>
      </AdminLayout>
    );
  }

  if (isEdit && fetchError) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto py-12 px-4 text-center">
          <p className="text-red-600 mb-4">Failed to load user data</p>
          <p className="text-gray-600 mb-6">{fetchError.message || "Please try again later."}</p>
          <Button onClick={() => navigate("/admin/users")}>
            Back to Users
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? "Edit User" : "Add New User"}
        </h1>

        <form 
          onSubmit={handleSubmit} 
          className="bg-white rounded-xl shadow-sm p-6 space-y-6"
        >
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter full name"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="user@example.com"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="08012345678"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            />
          </div>

          <div>
            <Label htmlFor="patientId">Hospital Number / Patient ID</Label>
            <Input
              id="patientId"
              name="patientId"
              value={formData.patientId}
              onChange={handleChange}
              placeholder="e.g. 4567890344"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            />
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/users")}
              disabled={addMutation.isLoading || updateMutation.isLoading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={addMutation.isLoading || updateMutation.isLoading}
            >
              {addMutation.isLoading || updateMutation.isLoading
                ? "Saving..."
                : isEdit
                ? "Update User"
                : "Create User"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminUserForm;